import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, of, ReplaySubject } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { AppStartupModel, AppStartupModelResponseResult, CountryViewModel } from '../../proxy/proxies';

export type { AppStartupModel, AppStartupModelResponseResult };

@Injectable({
    providedIn: 'root',
})
export class AppInitializerService {
    private readonly http = inject(HttpClient);
    private readonly translateService = inject(TranslateService);

    private _appStartupData: AppStartupModel | undefined;
    private initializationSubject = new ReplaySubject<boolean>(1);
    private initializationComplete = false;

    async initializeApp(): Promise<boolean> {
        if (this.initializationComplete) {
            return true;
        }
        return new Promise<boolean>((resolve) => {
            if (!this.initializationSubject.observed) {
                this.performAppStartupInitialization();
            }

            this.initializationSubject.subscribe((success) => {
                this.initializationComplete = true;
                resolve(success);
            });
        });
    }

    private performAppStartupInitialization(): void {
        const url = `${environment.apiBaseUrl}/v4.0/lookup/app-startup`;

        firstValueFrom(
            this.http.get<AppStartupModelResponseResult>(url).pipe(
                catchError((error) => {
                    console.error('Error fetching app startup data:', error);
                    return of(null);
                }),
                tap((response) => {
                    if (response?.result) {
                        this.configureAppStartupData(response.result);
                    } else {
                        this.setFallbackAppStartupData();
                    }
                }),
                finalize(() => {
                    this.initializationSubject.next(true);
                    this.initializationSubject.complete();
                }),
            ),
        );
    }

    private configureAppStartupData(data: unknown): void {
        this._appStartupData = AppStartupModel.fromJS(data);
    }

    private setFallbackAppStartupData(): void {
        this._appStartupData = undefined;
    }

    get appStartupData(): AppStartupModel | undefined {
        return this._appStartupData;
    }

    /** Countries from app startup lookup (`AppStartupModel.countries`). */
    get countries(): CountryViewModel[] {
        return this.appStartupData?.countries ?? [];
    }
}
